// Related documents, kept with the Service area. Two tabs:
//   • Printable form — a blank A4 boat ENTRY or EXIT form under the TECHNOHULL logo, printed via the
//     .doc-print-area isolation.
//   • Database — a log of boats arriving at / leaving the yard. Fill a record's fields (with date
//     pickers) and export either form pre-filled, as a clean A4 print (which also offers Save as PDF).
//     A record filled in from a Service vessel's Entry form button links back to that vessel; one that
//     holds the vessel's Vessel Entry & Work Control Form prints that form (vesselEntryPrint.ts) instead
//     of the entry/exit pair, and an edit here leaves that form as it was.
// The forms come in Greek (ΕΝΤΥΠΟ ΕΙΣΑΓΩΓΗΣ / ΕΞΑΓΩΓΗΣ ΣΚΑΦΟΥΣ), English, or both on one sheet (the
// default): the Form language switch in the header sets both tabs and is remembered on this device. The
// words are in boatDocCopy.ts.

import { useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MODEL_ORDER, type BoatDocRecord } from '@technohull/shared';
import {
  PageHeader, Segmented, EmptyState, InlineError, LoadingState, ErrorState, useConfirm, SearchInput,
} from '../components/ui.js';
import { IconPrinter, IconTrash, IconCompose } from '../components/icons.js';
import { useBoatDocs, useSaveBoatDoc, useDeleteBoatDoc } from '../features/hooks.js';
import { printBoatDoc } from '../features/boatDocPrint.js';
import { printVesselEntry } from '../features/vesselEntryPrint.js';
import { isControlForm } from '../features/vesselEntry.js';
import { CONTROL_FORM_NAME, PRINT_BLOCKED } from '../components/VesselEntryForm.js';
import { DOC_LANGS, boatDocView, useDocLang, type BoatDocKind, type DocLang, type DocLine } from '../features/boatDocCopy.js';
import { useAuth } from '../auth/AuthContext.js';
import { useDateFormatter, useFormatDate } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { fold, hasText } from '../lib/search.js';

type DocKind = BoatDocKind;

const langName = (l: DocLang) => DOC_LANGS.find((x) => x.value === l)!.name;

/** a piece of the form's text: its first line, then the translation under it (bilingual sheet). */
function Lines({ lines, cls }: { lines: DocLine[]; cls: string }) {
  return <>{lines.map((l, i) => <span key={l.lang} className={`${cls}${i ? ` ${cls}-alt` : ''}`} lang={l.lang}>{l.text}</span>)}</>;
}

export function Documents() {
  const [tab, setTab] = useState<'form' | 'records'>('form');
  const [lang, setLang] = useDocLang();
  return (
    <>
      <PageHeader title="Documents">
        <div className="doc-lang" role="group" aria-label="Form language">
          <span className="doc-lang-lbl" aria-hidden="true">Forms in</span>
          <Segmented
            value={lang}
            onChange={setLang}
            ariaLabel="Form language"
            options={DOC_LANGS.map((l) => ({ value: l.value, label: <span lang={l.value}>{l.label}</span>, title: `Print the forms in ${l.name}` }))}
          />
        </div>
        <Segmented
          value={tab}
          onChange={setTab}
          ariaLabel="Documents view"
          options={[
            { value: 'form', label: 'Printable form' },
            { value: 'records', label: 'Database' },
          ]}
        />
      </PageHeader>
      {tab === 'form' ? <FormTab lang={lang} /> : <RecordsTab lang={lang} />}
    </>
  );
}

// ── Printable form tab ────────────────────────────────────────────────────────────
function FormTab({ lang }: { lang: DocLang }) {
  const [kind, setKind] = useState<DocKind>('entry');
  return (
    <>
      <div className="doc-toolbar no-print">
        <Segmented
          value={kind}
          onChange={setKind}
          ariaLabel="Form type"
          options={[
            { value: 'entry', label: 'Vessel entry form' },
            { value: 'exit', label: 'Vessel exit form' },
          ]}
        />
        <button className="btn btn-sm btn-primary" onClick={() => window.print()}>
          <IconPrinter size={14} /> Print blank
        </button>
      </div>
      <div className="doc-print-area">
        <BlankSheet kind={kind} lang={lang} />
      </div>
    </>
  );
}

function BlankSheet({ kind, lang }: { kind: DocKind; lang: DocLang }) {
  const c = boatDocView(lang, kind);
  return (
    <section className="doc-sheet" lang={c.lang} aria-label={`Vessel ${kind} form, in ${langName(lang)}`}>
      <header className="doc-head">
        <img className="doc-logo" src="/technohull-logo-doc.png" alt="TECHNOHULL - Seaborn Performance" />
      </header>

      <h1 className="doc-title"><Lines lines={c.title} cls="doc-title-line" /></h1>
      <hr className="doc-rule" />

      <div className="doc-instr-h">{c.instrHeading}</div>
      {c.instr.map((l, i) => <p key={l.lang} className={`doc-instr${i ? ' doc-instr-alt' : ''}`} lang={l.lang}>{l.text}</p>)}

      <table className="doc-table">
        <tbody>
          {c.rows.map((r) => (
            <tr key={r.label[0]!.text}><td className="doc-k"><Lines lines={r.label} cls="doc-k-line" />{r.hint && <small>{r.hint}</small>}</td><td className="doc-v" /></tr>
          ))}
        </tbody>
      </table>

      <div className="doc-sign">
        <div className="doc-sign-box">
          <div className="doc-sign-line" />
          <Lines lines={c.sign} cls="doc-sign-cap" />
        </div>
      </div>

    </section>
  );
}

// ── Database tab ───────────────────────────────────────────────────────────────────
interface FormState { boatName: string; model: string; sap: string; phone: string; hin: string; entryDate: string; exitDate: string; note: string; }
const EMPTY: FormState = { boatName: '', model: '', sap: '', phone: '', hin: '', entryDate: '', exitDate: '', note: '' };

function RecordsTab({ lang }: { lang: DocLang }) {
  const q = useBoatDocs();
  const save = useSaveBoatDoc();
  const del = useDeleteBoatDoc();
  const fmtDate = useFormatDate();
  const df = useDateFormatter();
  const { can } = useAuth();
  const canWrite = can('service:write');
  const [confirmNode, confirm] = useConfirm();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [search, setSearch] = useState('');
  const [printErr, setPrintErr] = useState<Error | null>(null);
  const formRef = useRef<HTMLDivElement>(null);
  const firstField = useRef<HTMLInputElement>(null);
  // phone: the records are cards (tbl-cards) and the form waits behind "+ New record" after the search, so
  // finding a boat comes first. It opens by itself to edit a record or while it holds anything typed.
  const isPhone = useIsPhone();
  const formId = useId();
  const [formOpen, setFormOpen] = useState(false);

  const items = q.data?.items ?? [];
  const filtered = useMemo(() => {
    const s = fold(search.trim());
    if (!s) return items;
    return items.filter((r) => hasText(s, r.boatName, r.model, r.sap, r.hin, r.phone));
  }, [items, search]);
  // the model box suggests the production models first, then any other model already logged here
  // (boats from earlier ranges or other builders come in for service too), but stays free text. Spellings
  // that differ only in case are one model: the production name wins, otherwise the first one logged.
  const modelOptions = useMemo(() => {
    const seen = new Set<string>(MODEL_ORDER.map((m) => m.toLowerCase()));
    const extra: string[] = [];
    for (const r of items) {
      const m = r.model.trim();
      if (!m || seen.has(m.toLowerCase())) continue;
      seen.add(m.toLowerCase());
      extra.push(m);
    }
    extra.sort((a, b) => a.localeCompare(b));
    return [...MODEL_ORDER, ...extra];
  }, [items]);

  const set = (patch: Partial<FormState>) => setForm((f) => ({ ...f, ...patch }));
  const reset = () => { setForm(EMPTY); setEditingId(null); setFormOpen(false); save.reset(); };
  const canSave = !!(form.boatName.trim() || form.hin.trim());
  const formHasContent = editingId != null || Object.values(form).some((v) => v.trim());
  const formShown = formOpen || formHasContent;
  const revealForm = (focus: boolean) => requestAnimationFrame(() => {
    formRef.current?.scrollIntoView({ block: 'start' });
    if (focus) firstField.current?.focus({ preventScroll: true });
  });

  const startEdit = (r: BoatDocRecord) => {
    setEditingId(r.id);
    setForm({ boatName: r.boatName, model: r.model, sap: r.sap, phone: r.phone, hin: r.hin, entryDate: r.entryDate, exitDate: r.exitDate, note: r.note });
    save.reset();
    // .content (not window) is the scroller; default behaviour follows its CSS, so reduced motion is honoured.
    // A frame later, so a phone's collapsed form has opened before it is scrolled to.
    revealForm(true);
  };

  const onSave = () => {
    if (!canSave) return;
    save.mutate(
      { id: editingId ?? undefined, ...form },
      { onSuccess: () => reset() },
    );
  };

  const onDelete = async (r: BoatDocRecord) => {
    const ok = await confirm({
      title: 'Delete record',
      message: `Remove the record for ${r.boatName || r.hin || 'this boat'}? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    // deleting the record that's open in the form drops it out of edit mode (a later save would hit nothing).
    if (ok) del.mutate(r.id, { onSuccess: () => { if (editingId === r.id) reset(); } });
  };

  const exportDoc = (kind: DocKind, r: BoatDocRecord) =>
    setPrintErr(printBoatDoc(kind, r, fmtDate, lang) ? null : new Error(PRINT_BLOCKED));
  // a Service vessel's Vessel Entry & Work Control Form prints as that form (bilingual by design)
  const exportControl = (r: BoatDocRecord) =>
    setPrintErr(printVesselEntry(r, { date: (iso) => df.date(iso) }) ? null : new Error(PRINT_BLOCKED));

  if (q.isLoading) return <LoadingState label="Loading records…" />;
  if (q.isError && !q.data) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;

  return (
    <>
      {confirmNode}

      {canWrite && (
        <div className="doc-db-form" id={formId} data-collapsed={formShown ? undefined : ''} role="group" ref={formRef} aria-label={editingId ? 'Edit record' : 'New record'}>
          <div className="doc-db-form-hd">{editingId ? 'Edit record' : 'New record'}</div>
          <div className="doc-db-grid">
            <label className="doc-db-field">
              <span>Entry date</span>
              <input type="date" className="field" ref={firstField} value={form.entryDate} onChange={(e) => set({ entryDate: e.target.value })} />
            </label>
            <label className="doc-db-field">
              <span>Exit date</span>
              <input type="date" className="field" value={form.exitDate} onChange={(e) => set({ exitDate: e.target.value })} />
            </label>
            <label className="doc-db-field">
              <span>Boat name</span>
              <input className="field" value={form.boatName} maxLength={160} onChange={(e) => set({ boatName: e.target.value })} />
            </label>
            <label className="doc-db-field">
              <span>Model</span>
              <input className="field" list="doc-model-options" value={form.model} maxLength={80} onChange={(e) => set({ model: e.target.value })} placeholder="Pick or type a model" />
              <datalist id="doc-model-options">
                {modelOptions.map((m) => <option key={m} value={m} />)}
              </datalist>
            </label>
            <label className="doc-db-field">
              <span>SAP</span>
              <input className="field" value={form.sap} maxLength={40} onChange={(e) => set({ sap: e.target.value })} placeholder="SAP number" />
            </label>
            <label className="doc-db-field">
              <span>Phone</span>
              <input className="field" type="tel" value={form.phone} maxLength={60} onChange={(e) => set({ phone: e.target.value })} />
            </label>
            <label className="doc-db-field">
              <span>HIN</span>
              <input className="field" value={form.hin} maxLength={80} onChange={(e) => set({ hin: e.target.value })} placeholder="Hull identification number" />
            </label>
            <label className="doc-db-field doc-db-field-wide">
              <span>Note (internal)</span>
              <input className="field" value={form.note} maxLength={1000} onChange={(e) => set({ note: e.target.value })} placeholder="Optional, not printed on the forms" />
            </label>
          </div>
          <div className="doc-db-form-actions">
            {save.isError && <InlineError error={save.error} onDismiss={() => save.reset()} />}
            {!canSave && Object.values(form).some((v) => v.trim()) && <span className="muted" style={{ marginRight: 'auto', fontSize: 11 }}>Enter a boat name or HIN to save.</span>}
            {editingId && <button className="btn btn-ghost btn-sm" onClick={reset} disabled={save.isPending}>Cancel</button>}
            <button className="btn btn-primary btn-sm" onClick={onSave} disabled={!canSave || save.isPending}>
              {save.isPending ? 'Saving…' : editingId ? 'Save changes' : 'Add record'}
            </button>
          </div>
        </div>
      )}

      <div className="doc-db-bar no-print">
        <SearchInput value={search} onChange={setSearch} placeholder="Search boat, model, SAP, HIN or phone" width={300} />
        <span className="doc-db-count">{filtered.length} {filtered.length === 1 ? 'record' : 'records'}</span>
        {/* phones only (display:none above the PHONE tier, where the form is always open) */}
        {canWrite && !formHasContent && (
          <button
            type="button"
            className="btn btn-sm doc-db-newbtn"
            aria-expanded={formShown}
            aria-controls={formId}
            onClick={() => { const opening = !formOpen; setFormOpen(opening); if (opening) revealForm(false); }}
          >{formShown ? 'Hide form' : '+ New record'}</button>
        )}
      </div>

      {q.isError && q.data && <InlineError error={q.error} />}
      {del.isError && <InlineError error={del.error} onDismiss={() => del.reset()} />}
      {printErr && <InlineError error={printErr} onDismiss={() => setPrintErr(null)} />}

      {filtered.length === 0 ? (
        <EmptyState
          title={items.length === 0 ? 'No records yet' : 'No matching records'}
          hint={items.length === 0 ? (canWrite ? 'Add a boat above, then export its entry or exit form.' : 'Records added here will show up in this list.') : 'Try a different search.'}
        />
      ) : (
        // phone: the same rows as cards (the shared tbl-cards primitive, opted into there only, so the desktop
        // table keeps its own look)
        <div className={`doc-db-tablewrap${isPhone ? ' tbl-wrap' : ''}`}>
          <table className={`doc-db-table${isPhone ? ' tbl tbl-cards' : ''}`}>
            <thead>
              <tr>
                <th>Entry</th>
                <th>Exit</th>
                <th>Boat</th>
                <th>Model</th>
                <th>SAP</th>
                <th>Phone</th>
                <th>HIN</th>
                <th className="doc-db-actions-th">Forms</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={editingId === r.id ? 'doc-db-editing' : undefined}>
                  {/* card roles (phone): boat + model on line 1, the dates, SAP and HIN two-up under their
                      captions, the phone as a Call button, then the actions */}
                  <td className="tc-lbl" data-label="Entry">{r.entryDate ? fmtDate(r.entryDate) : <span className="doc-db-dash">-</span>}</td>
                  <td className="tc-lbl" data-label="Exit">{r.exitDate ? fmtDate(r.exitDate) : <span className="doc-db-dash">-</span>}</td>
                  <td className="doc-db-boat tc-title">
                    {r.boatName || <span className="doc-db-dash">-</span>}
                    {/* filled in from the vessel's Entry form button: back to the vessel on Service */}
                    {r.serviceRowId && (
                      <Link
                        className="doc-db-vlink"
                        to={`/service?vessel=${encodeURIComponent(r.serviceRowId)}`}
                        title="Kept with its vessel on the Service page - open it there"
                        aria-label={`Open ${r.boatName || 'this boat'} on the Service page`}
                      >Service</Link>
                    )}
                  </td>
                  <td className="doc-db-model tc-end">{r.model || <span className="doc-db-dash">-</span>}</td>
                  <td className="doc-db-sap tc-lbl" data-label="SAP">{r.sap || <span className="doc-db-dash">-</span>}</td>
                  <td className={`doc-db-phone${r.phone ? '' : ' tc-hide'}`}>
                    {isPhone && r.phone ? <a className="btn" href={`tel:${r.phone}`}>Call {r.phone}</a> : r.phone || <span className="doc-db-dash">-</span>}
                  </td>
                  <td className="doc-db-hin tc-lbl" data-label="HIN">{r.hin || <span className="doc-db-dash">-</span>}</td>
                  {/* flex lives on an inner div so the <td> stays a real table cell */}
                  <td className="tc-actions">
                    <div className="doc-db-actions">
                      {isControlForm(r) ? (
                        <button className="btn btn-ghost btn-xs" title="Print the vessel entry & work control form filled in, in Greek and English" onClick={() => exportControl(r)}>
                          <IconPrinter size={13} /> {CONTROL_FORM_NAME}
                        </button>
                      ) : (
                        <>
                          <button className="btn btn-ghost btn-xs" title={`Print the vessel entry form filled in, in ${langName(lang)}`} onClick={() => exportDoc('entry', r)}>
                            <IconPrinter size={13} /> Entry form
                          </button>
                          <button className="btn btn-ghost btn-xs" title={`Print the vessel exit form filled in, in ${langName(lang)}`} onClick={() => exportDoc('exit', r)}>
                            <IconPrinter size={13} /> Exit form
                          </button>
                        </>
                      )}
                      {canWrite && (
                        <>
                          <button className="btn btn-ghost btn-xs doc-db-icon" title="Edit" aria-label={`Edit record for ${r.boatName || r.hin || 'this boat'}`} onClick={() => startEdit(r)}>
                            <IconCompose size={13} />
                          </button>
                          <button className="btn btn-ghost btn-xs btn-del doc-db-del" title="Delete" aria-label={`Delete record for ${r.boatName || r.hin || 'this boat'}`} onClick={() => void onDelete(r)}>
                            <IconTrash size={13} /> Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
